import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, CalendarClock, CheckCircle2, ChevronRight, Copy, Mail, RefreshCw, Search, Target, UserPlus, Users, X } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import api from '../../services/api';

const CATEGORY_META = {
  potential_staff: { label: 'Potential Staff', detail: 'Candidates for support and operations roles', tone: 'blue' },
  potential_agent: { label: 'Potential Agents', detail: 'Prospects ready to operate and grow players', tone: 'violet' },
  potential_referral: { label: 'Potential Referrals', detail: 'People with networks, audiences, and reach', tone: 'green' },
};

const STATUS_LABELS = {
  new: 'New',
  follow_up: 'Follow-up',
  interested: 'Interested',
  qualified: 'Qualified',
  converted: 'Converted',
  disqualified: 'Disqualified',
};

const QUALIFICATION_META = {
  A: { label: 'Agent', detail: 'Direct recruitment and onboarding', tone: 'violet' },
  B: { label: 'Potential Agent', detail: 'Referral or reactivation follow-up', tone: 'green' },
  C: { label: 'Potential Staff', detail: 'Training and trainee pipeline', tone: 'blue' },
};

const PRIORITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };

const fmtDate = (value, fallback = '—') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const categoryLabel = (value) => CATEGORY_META[value]?.label || String(value || '').replaceAll('_', ' ');
const contactInfo = (lead) => {
  const method = lead.contact_method || (lead.email ? 'Email' : 'Phone');
  const value = method === 'Email'
    ? (lead.email || lead.contact_detail || '')
    : (lead.contact_detail || lead.phone || lead.email || '');
  return { method, value };
};

function LeadAnalyticsTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="hub-lead-chart-tooltip">
      <span>{fmtDate(`${label}T00:00:00Z`, label)}</span>
      <b>{payload[0]?.value || 0} leads</b>
    </div>
  );
}

export function LeadOverviewRow({ lead, onOpen }) {
  const { method, value } = contactInfo(lead);
  const country = lead.answers?.Country || 'Not provided';
  const contactTime = lead.answers?.['Preferred contact time'] || 'Not provided';
  const notes = lead.answers?.['Additional notes'];
  const imageStatus = lead.verification_image_available
    ? 'Image on file'
    : method === 'Email' ? 'Email (no image)' : 'No image on file';

  return (
    <tr onClick={() => onOpen(lead)}>
      <td><div className="hub-agent-cell"><span className="hub-avatar">{String(lead.full_name || '?').slice(0, 1).toUpperCase()}</span><div><b>{lead.full_name}</b><small>{lead.qualification_summary || 'Survey lead'}</small></div></div></td>
      <td className="hub-lead-contact-cell"><b>{method}</b><span>{value || 'Not provided'}</span></td>
      <td className="hub-lead-context-cell"><b>{country}</b><span>{contactTime}</span>{notes && <small title={String(notes)}>{notes}</small>}</td>
      <td className="hub-lead-verification-cell"><span className={lead.verification_image_available ? 'is-available' : ''}>{imageStatus}</span><button type="button" onClick={(event) => { event.stopPropagation(); onOpen(lead); }} aria-label={`View ${lead.full_name || 'lead'} details`}>{lead.verification_image_available ? 'View image' : 'View details'}</button></td>
      <td><span className="hub-lead-qualification"><b>{lead.qualification_category || '?'}</b><small>{lead.qualification_label || 'Legacy lead'}</small></span></td>
      <td><span className={`hub-lead-category is-${lead.category}`}>{categoryLabel(lead.category)}</span></td>
      <td><span className={`hub-lead-score is-${lead.score_band || 'cold'}`}><b>{lead.score || 0}</b><small>{lead.priority || lead.score_band || 'review'}</small></span></td>
      <td><span className={`hub-pill p-${lead.status}`}>{STATUS_LABELS[lead.status] || lead.status}</span></td>
      <td>{lead.assigned_agent_name || <span className="hub-cell-muted">Unassigned</span>}</td>
      <td><span>{lead.source || 'Survey'}</span><small className="hub-lead-cell-sub">{lead.campaign || ''}</small></td>
      <td>{fmtDate(lead.next_follow_up_at)}</td>
      <td>{fmtDate(lead.created_at)}</td>
    </tr>
  );
}

export function LeadDetailDrawer({ detail, agents, busy, onClose, onSave }) {
  const lead = detail?.lead || {};
  const [status, setStatus] = useState(lead.status || 'new');
  const [category, setCategory] = useState(lead.category || 'potential_referral');
  const [assignedAgentId, setAssignedAgentId] = useState(lead.assigned_agent_id || '');
  const [nextFollowUpAt, setNextFollowUpAt] = useState(
    lead.next_follow_up_at ? String(lead.next_follow_up_at).slice(0, 16) : '',
  );
  const [note, setNote] = useState('');
  const [verificationImageUrl, setVerificationImageUrl] = useState('');
  const [verificationImageError, setVerificationImageError] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  useEffect(() => setCopyStatus(''), [lead.id]);
  useEffect(() => {
    setVerificationImageUrl('');
    setVerificationImageError(false);
    if (!lead.id || !lead.verification_image_available) return undefined;
    let cancelled = false;
    const reader = new FileReader();
    reader.onload = () => { if (!cancelled) setVerificationImageUrl(String(reader.result || '')); };
    reader.onerror = () => { if (!cancelled) setVerificationImageError(true); };
    api.get(`/api/admin/agent-leads/${lead.id}/verification-image`, { responseType: 'blob' })
      .then(({ data }) => {
        if (cancelled) return;
        reader.readAsDataURL(data);
      })
      .catch(() => { if (!cancelled) setVerificationImageError(true); });
    return () => {
      cancelled = true;
      if (reader.readyState === 1) reader.abort();
    };
  }, [lead.id, lead.verification_image_available]);
  const availableAgents = useMemo(
    () => (agents || []).filter((agent) => !lead.tenant_id || agent.tenant_id === lead.tenant_id),
    [agents, lead.tenant_id],
  );
  const { method: contactMethod, value: contactValue } = contactInfo(lead);
  const contactAnswers = lead.answers || {};
  const country = contactAnswers.Country || 'Not provided';
  const contactTime = contactAnswers['Preferred contact time'] || 'Not provided';
  const extraNotes = contactAnswers['Additional notes'];

  const copyContact = async () => {
    try {
      await navigator.clipboard.writeText(contactValue);
      setCopyStatus('Contact copied');
    } catch {
      setCopyStatus('Could not copy. Select the contact above to copy it.');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    await onSave(lead.id, {
      status,
      category,
      assigned_agent_id: assignedAgentId,
      next_follow_up_at: nextFollowUpAt,
      note,
    });
    setNote('');
  };

  return (
    <div className="hub-drawer-backdrop" onClick={onClose}>
      <aside className="hub-drawer hub-lead-detail-drawer" onClick={(event) => event.stopPropagation()} aria-label="Lead details">
        <div className="hub-drawer-head">
          <div className="hub-drawer-id">
            <span className="hub-avatar"><Target /></span>
            <div>
              <h2>{lead.full_name || 'Lead'}</h2>
              <p>{lead.qualification_summary || `${categoryLabel(lead.category)} | Score ${lead.score || 0}/100`}</p>
            </div>
          </div>
          <button type="button" className="hub-icon-button" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="hub-tabs"><button type="button" className="is-active">Lead record</button></div>
        <div className="hub-drawer-body">
          <section className="hub-stat-grid">
            <div className="hub-stat"><small>Category</small><strong>{lead.qualification_category || 'Unscored'} {lead.qualification_label || ''}</strong></div>
            <div className="hub-stat"><small>Priority</small><strong>{lead.priority_label || lead.score_band || 'Unscored'} · {lead.score || 0}</strong></div>
            <div className="hub-stat"><small>Reason</small><strong>{lead.qualification_reason_label || 'Needs review'}</strong></div>
            <div className="hub-stat"><small>Owner</small><strong>{lead.assigned_agent_name || 'Unassigned'}</strong></div>
          </section>

          <section className="hub-panel hub-lead-routing-card">
            <small>Recommended team action</small>
            <b>{lead.workflow_label || 'Review and qualify manually'}</b>
            <span>{lead.classification_version === 'soagent-qna-v1' ? 'Calculated from the approved six-question survey model.' : 'Legacy lead awaiting the latest survey classification.'}</span>
          </section>

          <section className="hub-panel hub-lead-contact-card" aria-label="Applicant contact details">
            <div className="hub-lead-contact-main">
              <div>
                <small>Contact via {contactMethod}</small>
                <b className="hub-lead-contact-value">{contactValue || 'Not provided'}</b>
                <span className="hub-lead-contact-status" role="status" aria-live="polite">{copyStatus}</span>
              </div>
              {contactValue && <div className="hub-lead-contact-actions">
                <button type="button" className="hub-button hub-lead-copy-button" onClick={copyContact}><Copy /> Copy contact</button>
                {contactMethod === 'Email' && <a className="hub-button hub-lead-email-button" href={`mailto:${contactValue}`}><Mail /> Write email</a>}
              </div>}
            </div>
            <div className="hub-lead-contact-meta">
              <div><small>Country</small><b>{country}</b></div>
              <div><small>Best time to contact</small><b>{contactTime}</b></div>
              <div><small>Campaign</small><b>{lead.campaign || 'SO Agent survey'}</b></div>
            </div>
            {extraNotes && <div className="hub-lead-contact-note"><small>Applicant note</small><p>{extraNotes}</p></div>}
            <div className="hub-lead-verification-card">
              <div><small>Private contact verification</small><span>{contactMethod === 'Email' ? 'Email contact: no image required' : 'Required for new WhatsApp, Telegram, and Signal applications'}</span></div>
              {lead.verification_image_available ? (
                verificationImageUrl ? <img src={verificationImageUrl} alt="Applicant-provided contact QR code or profile image" /> : (
                  <p role="status">{verificationImageError ? 'Image unavailable. Refresh the lead to try again.' : 'Loading verification image…'}</p>
                )
              ) : <p>No verification image was saved for this lead. Contact details are still available above.</p>}
            </div>
          </section>

          <form className="hub-panel hub-form hub-lead-action-form" onSubmit={submit}>
            <div className="hub-panel-head">
              <div><h2>Qualification and follow-up</h2><p>Every save is recorded in the lead activity history.</p></div>
            </div>
            <div className="hub-form-row">
              <label><span>Category</span><select value={category} onChange={(event) => setCategory(event.target.value)}>{Object.entries(CATEGORY_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select></label>
              <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            </div>
            <div className="hub-form-row">
              <label><span>Assigned agent</span><select value={assignedAgentId} onChange={(event) => setAssignedAgentId(event.target.value)}><option value="">Unassigned</option>{availableAgents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name || agent.username || agent.email}</option>)}</select></label>
              <label><span>Next follow-up</span><input type="datetime-local" value={nextFollowUpAt} onChange={(event) => setNextFollowUpAt(event.target.value)} /></label>
            </div>
            <label><span>Contact note</span><textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="What happened, what they want, and the next action…" /></label>
            <div className="hub-lead-form-actions"><button type="submit" className="hub-button" disabled={busy}>{busy ? 'Saving…' : 'Save lead update'}</button></div>
          </form>

          <section className="hub-panel">
            <div className="hub-panel-head"><div><h2>Survey answers</h2><p>The signals used for automatic category and scoring.</p></div></div>
            {!!lead.score_reasons?.length && (
              <div className="hub-lead-score-reasons" aria-label="Lead score reasons">
                {lead.score_reasons.map((reason) => <span key={reason}><CheckCircle2 />{reason}</span>)}
              </div>
            )}
            <div className="hub-lead-answer-list">
              {Object.entries(lead.answers || {}).length ? Object.entries(lead.answers || {}).map(([question, answer]) => (
                <div key={question}><small>{question}</small><b>{Array.isArray(answer) ? answer.join(', ') : String(answer ?? '—')}</b></div>
              )) : <div className="hub-empty">No answer details were submitted.</div>}
            </div>
          </section>

          <section className="hub-panel">
            <div className="hub-panel-head"><div><h2>Activity history</h2><p>Source capture, ownership, notes, and status changes.</p></div></div>
            <div className="hub-lead-activity-list">
              {(detail.activities || []).map((item) => (
                <div key={item.id}><span><Activity /></span><div><b>{item.summary}</b><small>{item.actor || 'System'} · {fmtDate(item.created_at)}</small></div></div>
              ))}
              {!detail.activities?.length && <div className="hub-empty">No activity yet.</div>}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

export default function LeadCenterSection({ agents = [] }) {
  const [data, setData] = useState(null);
  const [category, setCategory] = useState('');
  const [qualificationCategory, setQualificationCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/admin/agent-leads/overview', {
        params: {
          category: category || undefined,
          qualification_category: qualificationCategory || undefined,
          priority: priority || undefined,
          status: status || undefined,
          q: query.trim() || undefined,
          limit: 200,
        },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lead Center data could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [category, qualificationCategory, priority, status, query]);

  useEffect(() => {
    const timer = window.setTimeout(() => load(), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  const openLead = async (lead) => {
    setSelected({ lead, activities: [] });
    try {
      const response = await api.get(`/api/admin/agent-leads/${lead.id}`);
      setSelected(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lead details could not be loaded');
    }
  };

  const saveLead = async (leadId, payload) => {
    setSaving(true);
    setError('');
    try {
      await api.patch(`/api/admin/agent-leads/${leadId}`, payload);
      const [detailResponse] = await Promise.all([
        api.get(`/api/admin/agent-leads/${leadId}`),
        load({ quiet: true }),
      ]);
      setSelected(detailResponse.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lead update failed');
    } finally {
      setSaving(false);
    }
  };

  const summary = data?.summary || {};
  const qualificationCategories = data?.qualification_categories || {};
  const leads = data?.leads || [];
  const chartData = data?.daily || [];

  return (
    <div className="hub-lead-center" data-testid="hub-lead-center">
      <section className="hub-lead-center-intro" aria-label="Lead Center overview">
        <div>
          <span className="hub-eyebrow">Growth operations</span>
          <h2>SO Agent leads, qualified systematically.</h2>
          <p>Survey answers classify every person as Potential Staff, Potential Agent, or Potential Referral, then score and route the next follow-up.</p>
        </div>
        <span className="hub-lead-source"><i /> SO Agent survey intake</span>
      </section>

      {error && <div className="hub-lead-error">{error}</div>}

      <section className="hub-lead-summary" aria-label="Lead summary">
        <article><span><Users /></span><div><small>Total leads</small><strong>{summary.total || 0}</strong><em>{summary.unassigned || 0} unassigned</em></div></article>
        <article><span><UserPlus /></span><div><small>New</small><strong>{summary.new || 0}</strong><em>Fresh intake</em></div></article>
        <article><span><CalendarClock /></span><div><small>Follow-up</small><strong>{summary.needs_follow_up || 0}</strong><em>{summary.overdue_follow_up || 0} overdue</em></div></article>
        <article><span><Target /></span><div><small>High priority</small><strong>{summary.high_priority || 0}</strong><em>Contact first</em></div></article>
        <article><span><CheckCircle2 /></span><div><small>Converted</small><strong>{summary.converted || 0}</strong><em>{summary.conversion_rate || 0}% conversion</em></div></article>
      </section>

      <section className="hub-lead-category-grid" aria-label="Lead categories">
        {Object.entries(QUALIFICATION_META).map(([value, meta]) => (
          <button key={value} type="button" data-tone={meta.tone} className={qualificationCategory === value ? 'is-active' : ''} onClick={() => setQualificationCategory(qualificationCategory === value ? '' : value)}>
            <span>Category {value} · {meta.label}</span><strong>{qualificationCategories[value] || 0}</strong><small>{meta.detail}</small><ChevronRight />
          </button>
        ))}
      </section>

      <div className="hub-lead-analytics-grid">
        <section className="hub-panel">
          <div className="hub-panel-head"><div><h2>Lead intake · 30 days</h2><p>Every accepted survey submission, day by day.</p></div><span className="hub-chip"><Activity /> <b>{summary.average_score || 0}</b> avg. score</span></div>
          <div className="hub-lead-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -24, bottom: 0 }}>
                <defs><linearGradient id="leadIntakeFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4056d8" stopOpacity={0.24} /><stop offset="100%" stopColor="#4056d8" stopOpacity={0.01} /></linearGradient></defs>
                <CartesianGrid vertical={false} stroke="#e7ebef" />
                <XAxis dataKey="day" tickFormatter={(value) => value.slice(5)} tickLine={false} axisLine={false} tick={{ fill: '#758092', fontSize: 10 }} minTickGap={26} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fill: '#758092', fontSize: 10 }} />
                <Tooltip content={<LeadAnalyticsTooltip />} />
                <Area type="monotone" dataKey="leads" stroke="#4056d8" strokeWidth={2} fill="url(#leadIntakeFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="hub-panel">
          <div className="hub-panel-head"><div><h2>Top sources</h2><p>Campaigns and forms producing the pipeline.</p></div></div>
          <div className="hub-lead-source-list">
            {(data?.sources || []).map((item) => <div key={item.source}><span><i />{item.source}</span><b>{item.count}</b></div>)}
            {!data?.sources?.length && <div className="hub-empty">No source data yet.</div>}
          </div>
        </section>
      </div>

      <section className="hub-panel hub-lead-worklist" aria-label="Lead worklist">
        <div className="hub-panel-head hub-lead-worklist-head">
          <div><h2>Lead worklist</h2><p>Highest score first. Search, filter, open, assign, and record every follow-up.</p></div>
          <button type="button" className="hub-icon-button" onClick={() => load()} aria-label="Refresh"><RefreshCw className={loading ? 'is-spinning' : ''} /></button>
        </div>
        <div className="hub-lead-filters">
          <label><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, email, phone, campaign, or owner" /></label>
          <select value={qualificationCategory} onChange={(event) => setQualificationCategory(event.target.value)}><option value="">All A/B/C categories</option>{Object.entries(QUALIFICATION_META).map(([value, meta]) => <option key={value} value={value}>Category {value}: {meta.label}</option>)}</select>
          <select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">All lead types</option>{Object.entries(CATEGORY_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select>
          <select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="">All priorities</option>{Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
          <select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </div>

        <div className="hub-table-wrap">
          <table className="hub-table hub-lead-table">
            <thead><tr><th>Lead</th><th>Contact</th><th>Country / time</th><th>Verification</th><th>Qualification</th><th>Type</th><th>Priority</th><th>Status</th><th>Owner</th><th>Source</th><th>Follow-up</th><th>Created</th></tr></thead>
            <tbody>
              {leads.map((lead) => <LeadOverviewRow key={lead.id} lead={lead} onOpen={openLead} />)}
              {!loading && !leads.length && <tr><td colSpan={12}><div className="hub-lead-zero"><span><UserPlus /></span><h3>No leads match this view</h3><p>SO Agent survey submissions will appear here automatically after intake.</p></div></td></tr>}
              {loading && !data && <tr><td colSpan={12}><div className="hub-empty">Loading Lead Center...</div></td></tr>}
            </tbody>
          </table>
        </div>
        <div className="hub-lead-pagination"><span>{data?.pagination?.total || 0} matching leads</span>{data?.pagination?.has_more && <span>Showing the highest-priority 200</span>}</div>
      </section>

      {selected && <LeadDetailDrawer detail={selected} agents={agents} busy={saving} onClose={() => setSelected(null)} onSave={saveLead} />}
    </div>
  );
}
